import { useParams } from 'react-router-dom';
import Container from '../../../../core/components/atoms/Container';
import PageHeader from '../../../../core/components/molecules/Header';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useFeedback } from '../../hooks/useFeedback';
import { Fragment, useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { differenceInCalendarDays, format } from 'date-fns';
import EssayCorrectedPaper from '../../components/CorrectionPaper';
import { CommentsSide } from '../../components/Comments';
import { ScoresHeader } from '../../components/Corrected/ScoresHeader';
import { ActionHeader } from '../../components/ActionsHeader';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import { Button } from '../../../../core/components/atoms/Button';
import ModalHowTo from '../../components/ModalHowTo';
import { ModalReview } from '../../components/ModalReview';
import ModalHistory from '../../components/ModalHistory';
import { Alert } from '../../../../core/components/atoms/Alert';
import { CorrectionImage } from '../../components/CorrectedImage';
import CorrectionInfoModal from '../../components/CorrectionInfoModal';
import { useMostraSeuAprovaTrigger } from '../../../../core/components/organism/MostraSeuAprovaModal/useMostraSeuAprovaTrigger';
import { RichTextComment } from '../../components/RichTextComment';

export default function CorrectedPage() {
  const { slug, tryNumber } = useParams();
  const { data, isPending } = useFeedback(slug || '', Number(tryNumber) || 1);

  const [howToOpen, setHowToOpen] = useState<boolean>(false);
  const [openInfo, setOpenInfo] = useState<boolean>(false);
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [reviewOpen, setReviewOpen] = useState<boolean>(false);

  const { fire: fireMostraSeuAprova } = useMostraSeuAprovaTrigger();
  // A tela é reaberta a cada consulta à correção; um disparo por tentativa basta.
  const firedEssay = useRef<string | null>(null);

  /**
   * Campanha #mostraseuaprova: a correção humana é o diferencial que a variação
   * `redacao` pede para o aluno mostrar, então o convite sai quando ela abre.
   *
   * Sai junto com a campanha — ver
   * core/components/organism/MostraSeuAprovaModal/README.md.
   */
  useEffect(() => {
    if (!data || !slug) return;

    const essayKey = `${slug}:${tryNumber ?? 1}`;

    if (firedEssay.current === essayKey) return;

    firedEssay.current = essayKey;

    fireMostraSeuAprova('redacao');
  }, [data, slug, tryNumber, fireMostraSeuAprova]);

  useEffect(() => {
    if(data) {
      const id = window.setTimeout(() => {
        if (
          data && (data.response_type === 'Revisão' && data.comentario_revisao === null) || 
          (data && data.response_type === 'Correção' && !data.sentReview) 
          && canReview()
        ) {
          setReviewOpen(true);
        }
      }, 30_000);

      return () => window.clearTimeout(id);
    }
  }, [data]);

  const onCloseReview = (action: string) => {
    if (action === 'reload') {
      window.location.reload();
    }

    setReviewOpen(false);
  };

  //TODO: Correções
  const handleFeedback = (comments: string) => {
    const parser = new DOMParser();

    const document = parser.parseFromString(comments || '', 'text/html');
    const items = document.querySelectorAll('span');
    const titles = document.querySelectorAll('[data-title]');
    const contents = document.querySelectorAll('[data-content]');

    const array: { title: string; content: string; className: string }[] = [];

    if (items.length > 0 && titles.length > 0 && contents.length > 0) {
      items.forEach((item, key) => {
        const title =
          (titles[key] &&
            titles[key].attributes &&
            titles[key]?.attributes?.getNamedItem('data-title')?.nodeValue) ||
          '';
        const content =
          (contents[key] &&
            contents[key]?.attributes &&
            contents[key]?.attributes?.getNamedItem('data-content')
              ?.nodeValue) ||
          '';

        const classTypes = !data?.image ? item.className : item.classList[1];
        const className = classTypes || '';

        if (!!title && !!content && !!className) {
          const newItem = {
            title,
            content,
            className,
          };

          array.push(newItem);
        }
      });
    }

    return JSON.stringify(array);
  };

  const adjustComments = () => {
    if (data?.provider === 'outros') {
      return JSON.stringify(data.comments);
    }

    if (data?.provider === 'redação_online') {
      return handleFeedback(data.correcao);
    }

    return data?.comments || '';
  };

  const showContent = () => {
    if (data?.provider === 'ferrari') {
      if (!data.image) {
        return (
          <EssayCorrectedPaper
            essayText={data?.rawText || ''}
            essayTitle=''
            fontSize='default'
            correction={data?.theme.correctionType || 2}
            provider={data?.provider || ''}
            comments={data?.comments || ''}
            isLoading={isPending}
          />
        );
      } else {
        return (
          <CorrectionImage
            rotation={data.rotate ?? 0}
            imageUrl={data.image || ''}
            imageMap={data.comment_map || ''}
            comments={data.comments}
          />
        );
      }
    }

    if (data?.provider === 'redação_online') {
      if (!data.image) {
        return (
          <EssayCorrectedPaper
            essayText={data?.correcao || ''}
            essayTitle=''
            fontSize='default'
            correction={data?.theme.correctionType || 2}
            provider={data?.provider || ''}
            comments={data?.comments || ''}
            isLoading={isPending}
          />
        );
      } else {
        return (
          <CorrectionImage
            rotation={data.rotate ?? 0}
            imageUrl={data.image || data.imageCorrection || ''}
            imageMap={data.correcao || data.comment_map}
            comments={[]}
          />
        );
      }
    }

    if (data?.provider === 'outros') {
      return (
        <CorrectionImage 
          rotation={data.rotate ?? 0} 
          imageUrl={data.imageCorrection || ''} 
          imageMap={data.correcao}
          comments={adjustComments()}
        />
      );
    }
  };

  const canReview = () => {
    if(data && data.corrigida_em) {
      const date = new Date(data?.corrigida_em);
      const diff = differenceInCalendarDays(new Date(), date);
      
      return diff > 7 ? false : true;
    }
    
    return false;
  }

  return (
    <Container
      gap='xxl'
      direction='col'
      type='updated'
      className='xs:mb-[30px] xl:mb-[30px]'
    >
      <Breadcrumbs
        items={[
          {
            label: 'Minhas redações',
            href: '/redacoes/minhas_redacoes',
          },
          {
            label: data?.theme?.theme || '',
            href: '#',
          },
        ]}
      />
      {isPending ? (
        Array.from({ length: 1 }, (_, key) => (
          <div key={key}>
            <ContentLoader
              backgroundColor={'#a7adba'}
              foregroundColor={'#c0c5ce'}
              viewBox='0 0 600 500'
            >
              <rect x='0' y='10' rx='2' ry='2' width='600' height='700' />
            </ContentLoader>
          </div>
        ))
      ) : (
        <Fragment>
          {data ? (
            <Fragment>
              {data.response_type === 'Correção' && data.sentReview && (
                <Alert
                  variant='success'
                  message='Sua avaliação foi enviada, aguarde uma resposta da moderação.'
                />
              )}
              {data.response_type === 'Revisão' && (
                <Alert
                  variant='warning'
                  message='A moderação do Aprova respondeu a sua avaliação desta correção. Veja mais detalhes abaixo.'
                />
              )}
              <PageHeader title={data?.theme?.theme || ''}>
                <div className='flex flex-row items-center gap-sm'>
                  <Typography size='small' className='italic'>
                    Corrigida em{' '}
                    {data.corrigida_em
                      ? format(new Date(data?.corrigida_em), 'dd/MM/yyyy')
                      : data.created_at
                        ? format(new Date(data?.created_at), 'dd/MM/yyyy')
                        : ''}
                  </Typography>
                  <Button
                    variant='text'
                    LeftIcon='update'
                    iconType='material'
                    iconVariant='rounded'
                    className='text-sm'
                    classNameIcon='text-md'
                    onClick={() => setHistoryOpen(true)}
                  >
                    Ver histórico de correções
                  </Button>
                </div>
              </PageHeader>
              <div className='flex flex-col gap-md'>
                <div className='bg-mono-color-white flex flex-col gap-xl rounded-md p-5 shadow-level-1'>
                  <div className='flex flex-row items-center gap-xxl'>
                    <div className='w-24'>
                      <Typography
                        type='titleSection'
                        size='h6'
                        className='text-center uppercase'
                      >
                        Nota final
                      </Typography>
                      <Typography
                        type='title'
                        size='h1'
                        className='text-center'
                      >
                        {Number.isInteger(data.finalScore)
                          ? data?.finalScore
                          : data?.finalScore.toFixed(2)}
                      </Typography>
                    </div>
                    <div className='flex-1'>
                      <Typography weight='bold'>Comentário final</Typography>
                      <Typography className='break-all italic'>
                        {data.provider === 'outros'
                          ? data?.comment
                          : data?.comentario_geral}
                      </Typography>
                    </div>
                  </div>
                  {(data.theme.correctionType === 3 ||
                    data.theme.correctionType === 4) && (
                    <Button
                      variant='text'
                      LeftIcon='info'
                      iconType='material'
                      iconVariant='rounded'
                      className='p-0 text-sm'
                      classNameIcon='text-md'
                      onClick={() => setHowToOpen(true)}
                    >
                      Como a nota da banca é calculada?
                    </Button>
                  )}
                </div>
                {data.title && (
                  <div className='flex flex-row justify-between items-center rounded-md bg-mono-color-white px-4 py-3'>
                    <Typography type='title' size='h3' className='text-mono-color-darkest'>
                      Título
                    </Typography>
                    <Typography className='font-light text-lg text-mono-color-darkest'> 
                      {data.title}
                    </Typography>
                  </div>
                )}
                <ScoresHeader
                  isOld={data.isOld}
                  scores={data.scores}
                  correction={data.theme.correctionType}
                />
                {data.provider === 'ferrari' && (
                  <ActionHeader
                    onReview={() => setReviewOpen(true)}
                    feedback={data}
                    hasReview={
                      (data.response_type === 'Revisão' &&
                        data.comentario_revisao !== null) ||
                      (data.response_type === 'Correção' && data.sentReview)
                    }
                    canReview={canReview()}
                  />
                )}
              </div>
              {data.response_type === 'Revisão' &&
                data.comentario_revisao !== null && (
                  <div className='flex flex-col gap-md bg-warning-100 px-4 py-3'>
                    <Typography weight='bold'>
                      Mensagem da moderação do aprova
                    </Typography>
                    <RichTextComment
                      className='break-all italic text-mono-color-black'
                      comment={data.comentario_revisao}
                    />
                    <Typography>
                      {format(
                        new Date(
                          data.revisions[data.revisions.length - 1].createdAt
                        ),
                        'dd/MM/yyyy'
                      )}{' '}
                      às{' '}
                      {format(
                        new Date(
                          data.revisions[data.revisions.length - 1].createdAt
                        ),
                        'HH:mm'
                      )}
                    </Typography>
                  </div>
                )}
              <div className='xs:mb-10 lg:mb-2'>
                <div className='mb-4'>
                  <Typography weight='bold' type='titleSection' size='h3'>
                    Redação
                  </Typography>
                  <Typography>
                    Clique nas marcações para ver os comentários
                  </Typography>
                </div>
                <div className='flex gap-xxl xs:flex-col lg:flex-row'>
                  <div className='xs:w-full lg:basis-2/3'>
                    {showContent()}
                    <Button
                      size='small'
                      LeftIcon="workspace_premium"
                      iconType='material'
                      iconSize='small'
                      variant='text'
                      className='text-button-color-tertiary-default mt-10'
                      onClick={() => setOpenInfo(true)}
                    >
                      Controle de qualidade das correções
                    </Button>
                  </div>
                  <div className='xs:w-full lg:basis-1/3'>
                    <CommentsSide
                      provider={data.provider}
                      comments={adjustComments()}
                      correction={data.theme.correctionType}
                    />
                  </div>
                </div>
              </div>
            </Fragment>
          ) : (
            <Typography className='text-center text-2xl'>
              Não foi possivel carregar sua redação
            </Typography>
          )}
        </Fragment>
      )}
      <CorrectionInfoModal 
        isOpen={openInfo}
        onClose={() => setOpenInfo(false)}
      />
      <ModalHowTo
        isOpen={howToOpen}
        onClose={() => setHowToOpen(false)}
        correction={data?.theme.correctionType || 3}
      />
      <ModalReview
        isOpen={reviewOpen}
        onClose={(action: string) => onCloseReview(action)}
        dataCorrected={data?.corrigida_em || new Date()}
        correctionId={data?._id || ''}
      />
      <ModalHistory
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={data?.revisions || []}
        correctionAt={data?.corrigida_em || new Date()}
      />
    </Container>
  );
}
